// Username generation + reservation for Pop Market.
//
// Format matches the PlayAware mobile app so both surfaces produce
// interchangeable handles: `{Adjective}{Noun}{digit(s)}` in CamelCase
// (e.g. AgileWolf1, EclipsePython3, TitanRaven12). Vocabulary was
// reverse-engineered from ~1,165 existing Usernames docs — the top
// 173 adjectives and 41 nouns the app actually uses in production.
//
// Auto-assignment ALWAYS generates a fresh random handle. The user's
// real name/email is never used as a default because those leak PII
// and produce ugly truncated names (e.g. "adityaveervikramsing").
// Users pick their own username on /role-pick or /me/edit.

import {
  doc, getDoc, runTransaction, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const MIN_LEN = 3;
const MAX_LEN = 20;

// Reserved handles — routes, brand terms, obvious admin.
const RESERVED = new Set([
  "admin", "support", "system", "api", "root", "null", "undefined",
  "pop", "market", "popmarket", "playaware", "pa",
  "seller", "buyer", "order", "orders", "chat", "forum",
  "signin", "signup", "login", "logout", "auth", "me", "post",
  "about", "calendar", "u", "user", "profile",
]);

// App vocabulary — extracted from live Usernames data on 2026-07-22.
// Ordered by real-world frequency (Eclipse most common, Rifle least).
const ADJECTIVES = [
  "Eclipse","Fog","Strike","Impact","Ultra","Brave","Fire","Flash","Lunar","Slick",
  "Titan","Vortex","Crazy","Frost","Gravity","Hades","Hidden","Hunter","Legend","Legendary",
  "Matrix","Nebula","Quick","Raider","Samurai","Shade","Stone","Zip","Agile","Ares",
  "Blast","Cool","Covert","Crypto","Digital","Epic","Fenrir","Flare","Infinite","Meteor",
  "Mist","Neo","Rush","Smoke","Soldier","Storm","Supreme","Titanus","Trigger","Void",
  "Wizard","Armor","Ash","Blade","Breach","Brute","Chaos","Combat","Cryptic","Dust",
  "Force","Glide","Hera","Iron","Oracle","Phantom","Pixel","Power","Recoil","Signal",
  "Sky","Sniper","Unknown","Warrior","Wind","Asteroid","Binary","Blaze","Bold","Classic",
  "Cloud","Comet","Dash","Earth","Fearless","Ghost","Horizon","Odin","Protocol","Royal",
  "Savage","Sharp","Solar","Spartan","Swift","Thor","Toxic","Unstoppable","Veil","Wild",
  "Alpha","Assault","Atlas","Bang","Beast","Bolt","Byte","Circuit","Core","Dark",
  "Drift","Flame","Frame","Fury","Grim","Knight","Lightning","Mad","Nimble","Ninja",
  "Orbit","Quantum","Rain","Relentless","Rift","Shield","Silent","Stellar","Tempo","Zenith",
  "Areson","Dominant","Ember","Galaxy","Kernel","Might","Node","Ocean","Photon","Quasar",
  "Rapid","Siege","Sonic","Turbo","Valkyrie","Wave","Zeus","Bullet","Cosmic","Cyber",
  "Draco","Fierce","Lava","Loki","Max","Night","Nova","Rock","Shadow","Specter",
  "Steel","System","Target","Thunder","Vector","Voidstar","Apollo","Ice","Logic","Rage",
  "Stealth","Syntax","Rifle",
];

const NOUNS = [
  "Wolf","Cheetah","Iguana","Anaconda","Python","Raven","Eagle","Dragon","Condor","Bison",
  "Lizard","Sparrow","Gorilla","Viper","Hawk","Phoenix","Jaguar","Cobra","Buffalo","Vulture",
  "Owl","Hippo","Leopard","Ape","Lion","Bull","Camel","Boar","Fox","Gecko",
  "Panther","Kraken","Falcon","Octopus","Crow","Orca","Tiger","Bear","Rhino","Monkey","Shark",
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Random CamelCase handle matching the app's format.
function randomHandle(suffixLen = 1) {
  const max = 10 ** suffixLen;
  const suffix = 1 + Math.floor(Math.random() * (max - 1)); // never 0
  return `${pick(ADJECTIVES)}${pick(NOUNS)}${suffix}`;
}

// Normalise raw user input for the input field. Preserves letters +
// digits + underscore, strips everything else, caps at MAX_LEN. Case
// is preserved (both the app and Firestore doc IDs are case-sensitive,
// so "AgileWolf1" and "agilewolf1" are distinct).
export function normalizeCandidate(raw) {
  return String(raw || "")
      .normalize("NFKD")
      .replace(/[^A-Za-z0-9_]/g, "")
      .slice(0, MAX_LEN);
}

// Client-side validation for a raw user-entered username.
export function usernameValidationError(raw) {
  const s = String(raw || "").trim();
  if (!s) return "Pick a username.";
  if (s.length < MIN_LEN) return `Too short (min ${MIN_LEN} characters).`;
  if (s.length > MAX_LEN) return `Too long (max ${MAX_LEN} characters).`;
  if (!/^[A-Za-z0-9_]+$/.test(s)) return "Only letters, numbers, and _";
  if (RESERVED.has(s.toLowerCase())) return "That name is reserved. Try another.";
  return null;
}

// Live availability check — case-insensitive so we don't hand out
// visually-identical handles that only differ in casing.
export async function checkUsernameAvailable(name, uid) {
  if (usernameValidationError(name)) return { available: false, ownedByMe: false };
  const snap = await getDoc(doc(db, "Usernames", name));
  if (!snap.exists()) return { available: true, ownedByMe: false };
  const ownedByMe = snap.data()?.uid === uid;
  return { available: ownedByMe, ownedByMe };
}

// Atomically reserve a candidate for `user`. Returns true if the
// reservation succeeded, false if the doc already exists.
async function reserve(candidate, user) {
  try {
    return await runTransaction(db, async (tx) => {
      const ref = doc(db, "Usernames", candidate);
      const snap = await tx.get(ref);
      if (snap.exists()) return false;
      tx.set(ref, {
        uid: user.uid,
        lowercaseUsername: candidate.toLowerCase(),
        createdAt: serverTimestamp(),
        source: "pop-market",
        displayName: user.displayName || null,
        photoURL: user.photoURL || null,
      });
      return true;
    });
  } catch (_) {
    return false;
  }
}

// Generate + reserve a fresh app-style handle for a new user. Ignores
// their real name entirely so nothing PII-adjacent lands on their
// public profile until they explicitly set it.
export async function assignUniqueUsername(user) {
  // Try increasingly-longer numeric suffixes so we scale gracefully
  // once the shorter combos are all taken: 1-digit (10) → 2-digit
  // (100) → 3-digit (1000) → 4-digit (10000).
  const attempts = [];
  for (let i = 0; i < 6; i++) attempts.push(randomHandle(1));
  for (let i = 0; i < 4; i++) attempts.push(randomHandle(2));
  for (let i = 0; i < 3; i++) attempts.push(randomHandle(3));
  for (let i = 0; i < 3; i++) attempts.push(randomHandle(4));

  for (const candidate of attempts) {
    if (candidate.length > MAX_LEN) continue;
    if (RESERVED.has(candidate.toLowerCase())) continue;
    if (await reserve(candidate, user)) return candidate;
  }

  // Absolute fallback keyed off the uid — effectively unreachable
  // given the ~200k×40×10 combination space of the primary attempts.
  const uidSlug = `Player${user.uid.slice(0, 6)}`;
  await reserve(uidSlug, user);
  return uidSlug;
}

// Atomically move a user's username from oldName → newName. Reserves
// the new doc, updates app_users, and deletes the old reservation
// inside one transaction so the collection is never in a half-state.
export async function renameUsername(uid, oldName, newName, publicSnapshot = {}) {
  if (!uid) throw new Error("renameUsername: missing uid");
  const err = usernameValidationError(newName);
  if (err) throw new Error(err);
  if (oldName && oldName === newName) return;

  await runTransaction(db, async (tx) => {
    const newRef = doc(db, "Usernames", newName);
    const oldRef = (oldName && oldName !== newName)
        ? doc(db, "Usernames", oldName)
        : null;

    // ALL READS FIRST (Firestore transaction contract).
    const reads = [tx.get(newRef)];
    if (oldRef) reads.push(tx.get(oldRef));
    const snaps = await Promise.all(reads);
    const newSnap = snaps[0];
    const oldSnap = oldRef ? snaps[1] : null;

    if (newSnap.exists()) {
      const owner = newSnap.data()?.uid;
      if (owner !== uid) throw new Error("That username is already taken.");
    } else {
      tx.set(newRef, {
        uid,
        lowercaseUsername: newName.toLowerCase(),
        createdAt: serverTimestamp(),
        source: "pop-market",
        ...publicSnapshot,
      });
    }
    if (oldSnap && oldSnap.exists() && oldSnap.data()?.uid === uid) {
      tx.delete(oldRef);
    }
    tx.set(
        doc(db, "app_users", uid),
        { username: newName, updatedAt: serverTimestamp() },
        { merge: true },
    );
  });
}
